import React from 'react';
import type { PositionMonthlyCost } from '../../utils/financesForecast';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);

interface PayrollCostBreakdownProps {
  cost: PositionMonthlyCost;
  sepRateLabel?: string;
  extrasNote?: string;
  ptoDays?: number;
  className?: string;
}

const PayrollCostBreakdown: React.FC<PayrollCostBreakdownProps> = ({
  cost,
  sepRateLabel,
  extrasNote,
  ptoDays,
  className = '',
}) => {
  const ptoLabel =
    ptoDays != null && ptoDays > 0 && cost.ptoDailyRate > 0
      ? `+ PTO backfill (${ptoDays} d @ ${formatCurrency(cost.ptoDailyRate)}/day)`
      : '+ PTO backfill';

  return (
    <div className={className}>
      <div className="fin-cost-row">
        <span className="fin-cost-row-label">Base salary</span>
        <span>{formatCurrency(cost.salary)}</span>
      </div>
      {cost.sepIra !== 0 || sepRateLabel ? (
        <div className="fin-cost-row">
          <span className="fin-cost-row-label">
            + SEP IRA{sepRateLabel ? ` (${sepRateLabel})` : ''}
          </span>
          <span>{formatCurrency(cost.sepIra)}</span>
        </div>
      ) : null}
      <div className="fin-cost-row">
        <span className="fin-cost-row-label">+ Health reimburse</span>
        <span>{formatCurrency(cost.healthReimbursement)}</span>
      </div>
      {cost.employerTaxIsFlatOverride ? (
        <div className="fin-cost-row">
          <span className="fin-cost-row-label">+ Employer tax (override)</span>
          <span>{formatCurrency(cost.employerTax)}</span>
        </div>
      ) : (
        <>
          <div className="fin-cost-row fin-cost-row-group-head">
            <span className="fin-cost-row-label">+ Employer payroll taxes</span>
            <span>{formatCurrency(cost.employerTax)}</span>
          </div>
          <div className="fin-cost-row fin-cost-row-nested">
            <span className="fin-cost-row-label">Social Security (6.2%)</span>
            <span>{formatCurrency(cost.employerTaxBreakdown.socialSecurity)}</span>
          </div>
          <div className="fin-cost-row fin-cost-row-nested">
            <span className="fin-cost-row-label">Medicare (1.45%)</span>
            <span>{formatCurrency(cost.employerTaxBreakdown.medicare)}</span>
          </div>
          <div className="fin-cost-row fin-cost-row-nested">
            <span className="fin-cost-row-label">FUTA</span>
            <span>{formatCurrency(cost.employerTaxBreakdown.futa)}</span>
          </div>
          <div className="fin-cost-row fin-cost-row-nested">
            <span className="fin-cost-row-label">
              SUTA ({cost.employerTaxBreakdown.state})
            </span>
            <span>{formatCurrency(cost.employerTaxBreakdown.suta)}</span>
          </div>
        </>
      )}
      {cost.ptoBackfill !== 0 || (ptoDays != null && ptoDays > 0) ? (
        <div className="fin-cost-row">
          <span className="fin-cost-row-label">{ptoLabel}</span>
          <span>{formatCurrency(cost.ptoBackfill)}</span>
        </div>
      ) : null}
      <div className="fin-cost-row">
        <span className="fin-cost-row-label">+ Extras{extrasNote ? ` (${extrasNote})` : ''}</span>
        <span>{formatCurrency(cost.extras)}</span>
      </div>
      <div className="fin-cost-row total">
        <span className="fin-cost-row-label">= Loaded / mo</span>
        <span>{formatCurrency(cost.total)}</span>
      </div>
    </div>
  );
};

export default PayrollCostBreakdown;
